// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetContext, SecondaryPage, Toast, ToastContext } from '@alto/design-system';
import { OrderDetailsLoading, PageErrorFallback } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { fetchAddresses } from '~shared/actions/addresses';
import { initializeExistingOrder, setOrderAtIndex, updateOrder } from '~shared/actions/checkout';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
// eslint-disable-next-line import/no-deprecated
import { fetchPrescriptions } from '~shared/actions/prescriptions';
// eslint-disable-next-line import/no-deprecated
import { fetchShipmentPaymentMethods } from '~shared/actions/shipmentPaymentMethods';
// eslint-disable-next-line import/no-deprecated
import { fetchShipments } from '~shared/actions/shipments';
import { closeCart } from '~shared/actions/ui/cart';
import { getExistingOrder } from '~shared/features/checkout/selectors/getOrder';
// eslint-disable-next-line import/no-deprecated
import { fetchShipmentTipsForUser } from '~shared/features/courier-tips/actions';
import { getShipmentTipForShipmentID } from '~shared/features/courier-tips/selectors';
import { getShipmentWithItemsByID } from '~shared/features/shipments/selectors/getShipmentsWithItems';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { useOrderPricing } from '~shared/queries';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { OrderDetails } from './OrderDetails';
import { EditOrAddTipModal } from '~web/features/courier-tips/components/EditOrAddTipModal';

type OrderDetailsRouteParams = {
  readonly orderID: string;
};

export const OrderDetailsContainer = () => {
  const { navigate } = useNavigation();
  const { orderID } = useParams<OrderDetailsRouteParams>();
  const shipmentID = Number(orderID);
  const dispatch = useDispatchShared();
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const user = useSelectorShared(getCurrentUser);
  const userID = user?.id;
  const { orderPricing, refetchOrderPricing } = useOrderPricing({ shipmentID });
  const shipment = useSelectorShared((state) =>
    getShipmentWithItemsByID(state, {
      id: shipmentID,
      orderPricing,
    }),
  );
  const existingOrder = useSelectorShared((state) => getExistingOrder(state, { shipmentID }));
  const shipmentTip = useSelectorShared((state) =>
    getShipmentTipForShipmentID(state, {
      shipmentID,
    }),
  );
  const fetchShipmentTipsError = useSelectorShared((state) => state.ui.errors.fetchShipmentTipsError);
  const isLoadingShipments = useSelectorShared((state) => state.ui.loading.fetchShipmentsLoading);

  useGetPaymentMethods();

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchAddresses());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchDeliveries());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchPrescriptions());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchShipments());
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchShipmentPaymentMethods());
  }, [dispatch, shipmentID, userID]);

  useEffect(() => {
    if (userID) {
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchShipmentTipsForUser(userID));
    }
  }, [dispatch, userID]);

  useEffect(() => {
    if (shipmentID) {
      refetchOrderPricing();
    }
  }, [dispatch, refetchOrderPricing, shipmentID]);

  useEffect(() => {
    if (fetchShipmentTipsError) {
      addToast(<Toast variant="error">Unable to fetch shipment tips. {fetchShipmentTipsError.details.message}</Toast>);
    }
  }, [addToast, fetchShipmentTipsError]);

  const goBack = () => {
    navigate('RouteAppTabNavigator', { screen: 'RouteTabOrders' });
  };

  if (isLoadingShipments) {
    return <OrderDetailsLoading />;
  }

  if (!shipment) {
    return (
      <SecondaryPage
        headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
        dismissIcon="chevronleft"
        onDismiss={goBack}
      >
        <PageErrorFallback />
      </SecondaryPage>
    );
  }

  const editShipment = () => {
    dispatch(setOrderAtIndex({ order: existingOrder, orderIndex: 0 }));
    dispatch(
      initializeExistingOrder({
        order: existingOrder,
        shipmentID,
      }),
    );
    dispatch(
      updateOrder({
        origin: 'Existing Order',
      }),
    );
    dispatch(closeCart());
    navigate('RouteCheckout');
  };

  const openCancelDeliveryPage = () => {
    navigate('RouteCancelOrdersForm', { shipmentID, search: `?shipmentID=${shipmentID}` });
  };

  const openEditOrAddTipModal = () => {
    setActiveActionSheet(<EditOrAddTipModal shipmentID={shipment.id} />);
  };

  return (
    <OrderDetails
      shipment={shipment}
      shipmentTip={shipmentTip}
      editShipment={editShipment}
      openCancelDeliveryPage={openCancelDeliveryPage}
      openEditOrAddTipModal={openEditOrAddTipModal}
    />
  );
};
